import type { Document } from '../../types/apiModels'
import { apiClient } from '../ruutsApi/client'
import endpoints from '../ruutsApi/endpoints'
import { unwrapCollection, unwrapSingle, unwrapVoid } from '../ruutsApi/normalize'

export type GetDocumentsParams = {
  entityId: string
  entityTypeId?: number
  documentsTypes?: number[]
  signal?: AbortSignal
}

export type DocumentPayload = {
  id?: string
  entityId: string
  entityTypeId: number
  documentTypeId: number
  url: string
}

// GET /v1/documents spreads req.query straight into the Sequelize `where`, so any query param
// that is not a Document column (there is no `documentsTypes` column — only `documentTypeId`)
// makes the backend answer 500 "column Document.documentsTypes does not exist". Until the API
// exposes a real multi-type filter, the type narrowing has to happen client-side.
export const getDocumentsByEntity = async ({
  entityId,
  entityTypeId,
  documentsTypes,
  signal,
}: GetDocumentsParams): Promise<Document[]> => {
  const response = await apiClient.get(endpoints.documents, {
    params: { entityId, entityTypeId },
    signal,
  })
  const documentsFromEntity = unwrapCollection<Document>(response)
  return documentsTypes?.length
    ? documentsFromEntity.filter(document => documentsTypes.includes(document.documentTypeId))
    : documentsFromEntity
}

export const createDocument = async ({
  document,
}: {
  document: DocumentPayload | DocumentPayload[]
}): Promise<Document | Document[]> => {
  const response = await apiClient.post(endpoints.documents, document)
  return Array.isArray(document)
    ? unwrapCollection<Document>(response)
    : unwrapSingle<Document>(response)
}

export const updateDocument = async ({
  document,
}: {
  document: DocumentPayload | DocumentPayload[]
}): Promise<void> => {
  const response = await apiClient.put(endpoints.documents, document)
  return unwrapVoid(response)
}

export const removeDocuments = async ({
  documents,
}: {
  documents: { id: string }[]
}): Promise<void> => {
  const documentIds = documents.map(doc => doc.id)
  await apiClient.delete(endpoints.documents, { data: documentIds })
}
